import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { overAllowanceMachineIds } from '../../lib/machineAllowance';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import { useTranslation } from '../../i18n/LanguageContext';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import {
  IconTractor,
  IconSearch,
  IconFilter,
  IconPlus,
  IconLock,
  IconLayoutGrid,
  IconList,
} from '@tabler/icons-react';

const CATEGORIES = [
  { value: '', key: 'cat.all' },
  { value: 'agricultural', key: 'cat.agricultural' },
  { value: 'groundcare', key: 'cat.groundcare' },
  { value: 'plant', key: 'cat.plant' },
  { value: 'vehicle', key: 'cat.vehicle' },
  { value: 'atv', key: 'cat.atv' },
];

const ROW_PULSE = `
@keyframes agw-row-pulse {
  0%, 100% { background-color: rgba(163, 45, 45, 0.06); }
  50%      { background-color: rgba(163, 45, 45, 0.16); }
}
.agw-blocked-row { animation: agw-row-pulse 2.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .agw-blocked-row { animation: none; background-color: rgba(163, 45, 45, 0.12); }
}`;

export default function MachineList() {
  const { t } = useTranslation();
  const { orgId, userProfile, role, org } = useAuth();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [view, setView] = useState('list');

  const siteId = userProfile?.siteId;

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [machinesSnap, sitesSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'machines')),
          getDocs(collection(db, 'organisations', orgId, 'sites')),
        ]);

        let allMachines = machinesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Managers only see their site
        if (role === 'manager' && siteId) {
          allMachines = allMachines.filter(m => m.siteId === siteId);
        }

        setMachines(allMachines);
        setSites(sitesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId, role]);

  // Presentation only. Greyed when a payment has failed, never a gate on
  // anything — see lib/machineAllowance.
  const overAllowance = overAllowanceMachineIds(machines, org);

  const filtered = machines.filter(m => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      m.makeLabel?.toLowerCase().includes(q) ||
      m.modelLabel?.toLowerCase().includes(q) ||
      m.serialNumber?.toLowerCase().includes(q) ||
      m.registration?.toLowerCase().includes(q);
    const matchCategory = !filterCategory || m.category === filterCategory;
    const matchSite = !filterSite || m.siteId === filterSite;
    const matchStatus = !filterStatus ||
      (filterStatus === 'blocked' && m.isBlocked) ||
      (filterStatus === 'active' && !m.isBlocked);
    return matchSearch && matchCategory && matchSite && matchStatus;
  });

  const blocked = filtered.filter(m => m.isBlocked).length;

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name || '—';

  const categoryIcon = (cat) => {
    switch (cat) {
      case 'vehicle': return '🚐';
      case 'atv': return '🏍️';
      case 'plant': return '🏗️';
      case 'groundcare': return '🌿';
      default: return '🚜';
    }
  };

  return (
    <Layout>
      <style>{ROW_PULSE}</style>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title={t('machines.title')}
          subtitle={`${filtered.length} ${t(filtered.length === 1 ? 'machines.count_one' : 'machines.count_many')} · ${blocked} ${t('machines.blocked')}`}
          actions={
            <>
              <div style={{ display: 'flex', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                <div
                  onClick={() => setView('list')}
                  style={{ padding: '6px 10px', cursor: 'pointer', background: view === 'list' ? 'var(--color-brand)' : 'transparent', color: view === 'list' ? '#fff' : 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center' }}
                >
                  <IconList size={15} stroke={1.5} />
                </div>
                <div
                  onClick={() => setView('grid')}
                  style={{ padding: '6px 10px', cursor: 'pointer', background: view === 'grid' ? 'var(--color-brand)' : 'transparent', color: view === 'grid' ? '#fff' : 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center' }}
                >
                  <IconLayoutGrid size={15} stroke={1.5} />
                </div>
              </div>
              <Button
                variant="primary"
                icon={<IconPlus size={14} stroke={1.5} />}
                onClick={() => navigate('/machines/new')}
              >
                {t('machines.add')}
              </Button>
            </>
          }
        />

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: 200 }}>
            <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder={t('machines.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            style={{ fontSize: 13, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', minWidth: 140 }}
          >
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{t(c.key)}</option>)}
          </select>
          {role === 'org_admin' && (
            <select
              value={filterSite}
              onChange={e => setFilterSite(e.target.value)}
              style={{ fontSize: 13, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', minWidth: 140 }}
            >
              <option value="">{t('machines.all_sites')}</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ fontSize: 13, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', minWidth: 120 }}
          >
            <option value="">{t('machines.all_statuses')}</option>
            <option value="active">{t('machines.available')}</option>
            <option value="blocked">{t('machines.blocked_one')}</option>
          </select>
          {(search || filterCategory || filterSite || filterStatus) && (
            <button
              onClick={() => { setSearch(''); setFilterCategory(''); setFilterSite(''); setFilterStatus(''); }}
              style={{ fontSize: 12, color: 'var(--color-brand)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}
            >
              Clear filters
            </button>
          )}
        </div>

        {/* List view */}
        {view === 'list' && (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.8fr 60px', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['machines.col_machine', 'machines.col_serial', 'machines.col_site', 'machines.col_hours', 'machines.col_status', ''].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h ? t(h) : ''}</div>
              ))}
            </div>

            {loading ? (
              <LoadingSkeleton rows={6} />
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<IconTractor size={32} stroke={1.5} />}
                title={t('machines.none_found')}
                message={search || filterCategory || filterStatus ? 'Try adjusting your filters' : 'Add your first machine to get started'}
                action={!search && !filterCategory && !filterStatus && (
                  <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/machines/new')}>
                    {t('machines.add')}
                  </Button>
                )}
              />
            ) : filtered.map((machine, i) => {
              const dimmed = overAllowance.has(machine.id);
              return (
              <div key={machine.id} style={{ borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
              {dimmed && (
                <div style={{
                  margin: '10px 16px 0', background: '#fff',
                  border: '0.5px solid var(--color-border-light)',
                  borderRadius: 'var(--radius-sm)', padding: '6px 10px',
                  fontSize: 11, color: 'var(--color-text-secondary)',
                }}>
                  {t('billing.over_limit_row')}
                </div>
              )}
              <div
                onClick={() => navigate(`/machines/${machine.id}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 0.8fr 60px',
                  gap: 0,
                  padding: '12px 16px',
                  cursor: 'pointer',
                  background: 'transparent',
                  alignItems: 'center',
                  opacity: dimmed ? 0.45 : 1,
                  filter: dimmed ? 'grayscale(1)' : 'none',
                }}
                className={machine.isBlocked ? 'agw-blocked-row' : undefined}
                onMouseEnter={e => !machine.isBlocked && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                onMouseLeave={e => !machine.isBlocked && (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-md)',
                    background: machine.isBlocked ? 'var(--color-danger-light)' : '#E6F1FB',
                    border: machine.isBlocked ? '0.5px solid var(--color-danger)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 16,
                  }}>
                    {machine.imageURL ? (
                      <img src={machine.imageURL} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                    ) : machine.isBlocked ? (
                      <IconLock size={14} color="var(--color-danger)" stroke={1.5} />
                    ) : (
                      <span>{categoryIcon(machine.category)}</span>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 400, color: machine.isBlocked ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                      {machine.makeLabel} {machine.modelLabel}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>
                      {machine.category} · {machine.year || '—'}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  {machine.registration || machine.serialNumber || '—'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {getSiteName(machine.siteId)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {machine.currentHours ? `${machine.currentHours.toLocaleString()} hrs` : '—'}
                </div>
                <StatusPill status={machine.isBlocked ? 'blocked' : 'active'} />
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>{t('machines.view')}</div>
              </div>
              </div>
              );
            })}
          </Card>
        )}

        {/* Grid view */}
        {view === 'grid' && (
          loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', height: 180 }} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card>
              <EmptyState
                icon={<IconTractor size={32} stroke={1.5} />}
                title={t('machines.none_found')}
                message="Try adjusting your filters"
              />
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {filtered.map(machine => {
                const dimmed = overAllowance.has(machine.id);
                return (
                <Card
                  key={machine.id}
                  onClick={() => navigate(`/machines/${machine.id}`)}
                  hover
                  style={{ border: `0.5px solid ${machine.isBlocked ? 'var(--color-danger)' : 'var(--color-border-light)'}` }}
                >
                  {dimmed && (
                    <div style={{
                      background: '#fff', border: '0.5px solid var(--color-border-light)',
                      borderRadius: 'var(--radius-sm)', padding: '6px 9px', marginBottom: 10,
                      fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.5,
                    }}>
                      {t('billing.over_limit_row')}
                    </div>
                  )}
                  <div style={{ opacity: dimmed ? 0.45 : 1, filter: dimmed ? 'grayscale(1)' : 'none' }}>
                  {/* Machine image */}
                  <div style={{
                    height: 80,
                    background: machine.isBlocked ? 'var(--color-danger-light)' : 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                    fontSize: 36,
                  }}>
                    {machine.imageURL ? (
                      <img src={machine.imageURL} alt="" style={{ height: 64, objectFit: 'contain' }} />
                    ) : machine.isBlocked ? (
                      <IconLock size={28} color="var(--color-danger)" stroke={1.5} />
                    ) : (
                      <span>{categoryIcon(machine.category)}</span>
                    )}
                  </div>

                  <div style={{ fontSize: 14, fontWeight: 400, color: machine.isBlocked ? 'var(--color-danger)' : 'var(--color-text-primary)', marginBottom: 4 }}>
                    {machine.makeLabel} {machine.modelLabel}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
                    {machine.registration || machine.serialNumber || '—'} · {getSiteName(machine.siteId)}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                      {machine.currentHours ? `${machine.currentHours.toLocaleString()} hrs` : 'Hours not recorded'}
                    </div>
                    <StatusPill status={machine.isBlocked ? 'blocked' : 'active'} />
                  </div>
                  </div>
                </Card>
                );
              })}
            </div>
          )
        )}
      </div>
    </Layout>
  );
}